"use client";

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { MODEL_COLORS, MODEL_REGISTRY, LANGUAGES } from "@/lib/types";

interface CrossModelChartProps {
  data: Record<string, Record<string, Record<string, number>>>; // { modelId: { ckpt: { lang: wer } } }
  selectedCheckpoints: Record<string, string>; // { modelId: ckpt }
  title: string;
  height?: number;
}

export default function CrossModelChart({ data, selectedCheckpoints, title, height = 360 }: CrossModelChartProps) {
  const activeModels = Object.keys(selectedCheckpoints).filter(
    (m) => data[m] && data[m][selectedCheckpoints[m]]
  );

  const chartData = [...LANGUAGES].map((lang) => {
    const row: Record<string, string | number> = { language: lang };
    for (const modelId of activeModels) {
      const ckpt = selectedCheckpoints[modelId];
      row[modelId] = data[modelId]?.[ckpt]?.[lang] ?? 0;
    }
    return row;
  });

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5">
      <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="language"
            tick={{ fontSize: 10 }}
            tickFormatter={(v: string) => v.slice(0, 3).toUpperCase()}
            axisLine={false}
            tickLine={false}
          />
          <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
          <Tooltip
            formatter={(value: unknown, name: unknown) => [`${Number(value).toFixed(2)}%`, MODEL_REGISTRY[String(name)] || String(name)]}
            contentStyle={{
              background: "#18181b",
              border: "1px solid #3f3f46",
              borderRadius: "8px",
              fontSize: "11px",
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: "10px", paddingTop: "8px" }}
            formatter={(value: string) => `${MODEL_REGISTRY[value]?.split("-")[0] || value} (${selectedCheckpoints[value]})`}
          />
          {activeModels.map((modelId) => (
            <Bar
              key={modelId}
              dataKey={modelId}
              fill={MODEL_COLORS[modelId] || "#6366f1"}
              radius={[3, 3, 0, 0]}
              maxBarSize={28}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
